/**
 * SignContractDialog — contract-SIGN FE-slice increment #2 (Javo-ratify 2026-05-31).
 *
 * A pult-mediált aláírás-flow FE-orchestrátora: a dolgozó a tableten megnyitja,
 * a session a háttérben nyílik (`createSigningSession`), az átvevő a `SignaturePad`-en
 * aláír, megadja a 2 kötelező consent-et, majd a `submitSignature` beküldi a canvas-payloadot.
 *
 * NB: NEM self-service — a dolgozó indítja a pultnál, az ID-beazonosítás (CameraCaptureDialog)
 * + a szerződés-PDF egy korábbi lépés. A signed-PDF *archive* (Story 100-9) Phase-2 (Javo-ratify).
 *
 * In-flight guard + mount-tracker + reset-on-open a PermanentGrantDialog (B-13) mintáját követi.
 */
import { createSigningSession, submitSignature, type SigningSourceType } from '@/api/esign';
import { Button } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from '@kgc/i18n';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import * as React from 'react';
import { SignaturePad, type SignatureResult } from './SignaturePad';

/** A canvas-payload (dataURL) megengedett max mérete — a BE `SubmitSignatureSchema` 512 KiB-et véd.
 *  FE-oldali előzetes guard (Zeno #170-review javaslata): korai user-feedback a néma 413 helyett. */
const MAX_PAYLOAD_BYTES = 512 * 1024;

/** A szerződés-típus → esign forrás-típus leképezés (a `@kgc/esign` enumjához). */
const SOURCE_TYPE_BY_CONTRACT: Record<string, SigningSourceType> = {
  rental: 'RENTAL_CONTRACT',
  'rental-return': 'RENTAL_RETURN',
  service: 'WORKSHEET',
  sales: 'DELIVERY_NOTE',
};

export interface SignContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A forrás-szerződés azonosítói. */
  contract: { id: string; contractNumber: string; type: string };
  /** Sikeres aláírás után hívódik (pl. a szülő `refetch`-eli a szerződést). */
  onSigned?: () => void;
}

function byteLength(s: string): number {
  // TextEncoder jsdom-safe; a `.length` UTF-16-kódegység, nem byte → ez a helyes méret.
  return new TextEncoder().encode(s).length;
}

export function SignContractDialog({
  open,
  onOpenChange,
  contract,
  onSigned,
}: SignContractDialogProps) {
  const { t } = useTranslation();

  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [sessionLoading, setSessionLoading] = React.useState(false);
  const [signature, setSignature] = React.useState<SignatureResult | null>(null);
  const [basicConsent, setBasicConsent] = React.useState(false);
  const [dataConsent, setDataConsent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);

  const inFlightRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset + session-nyitás open felfutó élén (deps = [open]). `false`-ról indul, így a
  // mount-while-open eset (pl. teszt / feltételes mount) is triggerel — nem csak a false→true váltás.
  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSessionId(null);
      setSignature(null);
      setBasicConsent(false);
      setDataConsent(false);
      setSubmitError(null);
      setDone(false);

      setSessionLoading(true);
      const sourceType = SOURCE_TYPE_BY_CONTRACT[contract.type] ?? 'RENTAL_CONTRACT';
      createSigningSession({ sourceType, sourceId: contract.id })
        .then(s => {
          if (isMountedRef.current) setSessionId(s.id);
        })
        .catch((ex: unknown) => {
          const msg = ex instanceof Error ? ex.message : String(ex);
          if (isMountedRef.current) setSubmitError(msg);
        })
        .finally(() => {
          if (isMountedRef.current) setSessionLoading(false);
        });
    }
    prevOpenRef.current = open;
  }, [open, contract.id, contract.type]);

  const payload = signature && !signature.isEmpty ? signature.payload : '';
  const payloadTooLarge = payload.length > 0 && byteLength(payload) > MAX_PAYLOAD_BYTES;
  const canSubmit =
    !!sessionId &&
    payload.length > 0 &&
    !payloadTooLarge &&
    basicConsent &&
    dataConsent &&
    !submitting &&
    !done;

  const handleSubmit = React.useCallback(async () => {
    if (inFlightRef.current || !sessionId || !signature || signature.isEmpty) return;
    if (byteLength(signature.payload) > MAX_PAYLOAD_BYTES) {
      setSubmitError(t('esign.dialog.payloadTooLarge'));
      return;
    }
    if (!basicConsent || !dataConsent) {
      setSubmitError(t('esign.dialog.consentRequired'));
      return;
    }
    inFlightRef.current = true;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await submitSignature(sessionId, {
        format: 'png',
        payload: signature.payload,
        consent: { basicAcknowledged: true, dataProcessingAcknowledged: true },
        clientMetadata: {
          strokeCount: signature.strokeCount,
          durationMs: signature.durationMs,
        },
      });
      if (isMountedRef.current) {
        setDone(true);
        onSigned?.();
      }
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : String(ex);
      if (isMountedRef.current) setSubmitError(msg);
    } finally {
      inFlightRef.current = false;
      if (isMountedRef.current) setSubmitting(false);
    }
  }, [sessionId, signature, basicConsent, dataConsent, onSigned, t]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (inFlightRef.current) return;
      onOpenChange(next);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="sign-contract-dialog">
        <DialogHeader>
          <DialogTitle>
            {t('esign.dialog.title')} — {contract.contractNumber}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div
            className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-3 text-sm text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-200"
            data-testid="sign-contract-success"
          >
            <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span>{t('esign.dialog.success')}</span>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            {submitError && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"
                data-testid="sign-contract-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <span>{submitError}</span>
              </div>
            )}

            {sessionLoading ? (
              <div
                className="flex items-center gap-2 text-sm text-muted-foreground"
                data-testid="sign-contract-session-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>{t('esign.dialog.sessionLoading')}</span>
              </div>
            ) : (
              <>
                <SignaturePad
                  disabled={submitting || !sessionId}
                  onChange={setSignature}
                  clearLabel={t('common.clear')}
                  hintLabel={t('esign.dialog.padHint')}
                />

                {payloadTooLarge && (
                  <p className="text-xs text-red-600" data-testid="sign-contract-too-large">
                    {t('esign.dialog.payloadTooLarge')}
                  </p>
                )}

                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={basicConsent}
                    disabled={submitting}
                    onChange={e => setBasicConsent(e.target.checked)}
                    data-testid="sign-contract-consent-basic"
                  />
                  <span>{t('esign.consent.basicLabel')}</span>
                </label>

                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={dataConsent}
                    disabled={submitting}
                    onChange={e => setDataConsent(e.target.checked)}
                    data-testid="sign-contract-consent-data"
                  />
                  <span>{t('esign.consent.dataProcessingLabel')}</span>
                </label>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={submitting}
            onClick={() => handleOpenChange(false)}
            data-testid="sign-contract-cancel"
          >
            {done ? t('common.close') : t('common.cancel')}
          </Button>
          {!done && (
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={() => void handleSubmit()}
              data-testid="sign-contract-submit"
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              {submitting ? t('common.saving') : t('esign.action.sign')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
